import React from 'react';
import { observer } from 'mobx-react-lite';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { styled } from '@mui/material/styles';
import { marked } from 'marked';

const DEFAULT_TEMPLATE = `<h1>Notes</h1>
<ul>
  <li></li>
  <li></li>
  <li></li>
</ul>
<h1>Tasks</h1>
<ul>
  <li><input type="checkbox" checked> Task 1</li>
  <li><input type="checkbox"> Task 2</li>
  <li><input type="checkbox"> Task 3</li>
</ul>
<h1>Links</h1>
<ul>
  <li></li>
  <li></li>
  <li></li>
</ul>`;

const EditorContainer = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '.ql-toolbar': {
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: 'none',
    backgroundColor: theme.palette.background.default,

    '.ql-stroke, .ql-picker-label': {
      stroke: theme.palette.text.primary,
      color: theme.palette.text.primary,
    },

    '.ql-formats button': {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: 'white',
        '.ql-stroke': {
          stroke: 'white',
        },
      },
    },
    '.ql-picker-label:hover': {
      color: 'white',
      '.ql-stroke': {
        stroke: 'white',
      },
    },
    '.ql-active': {
      backgroundColor: theme.palette.action.selected,
      color: 'white !important',
      '.ql-stroke': {
        stroke: 'white !important',
      },
    },
  },
  '.ql-container': {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
  },
  '.ql-editor': {
    flex: 1,
    height: 'auto',
    overflowY: 'auto',
    padding: '16px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: theme.palette.text.primary,
    '&::before': {
      color: theme.palette.text.secondary,
      fontStyle: 'normal',
    },
    a: {
      color: theme.palette.primary.light,
    },
  },
}));

interface ProjectNotesProps {
  content: string;
  onChange: (content: string) => void;
}

export const ProjectNotes = observer(({ content, onChange }: ProjectNotesProps) => {
  const [isConverted, setIsConverted] = React.useState(false);

  const isHtml = React.useMemo(() => /<[a-z][\s\S]*>/i.test(content), [content]);

  React.useEffect(() => {
    // One-time conversion from markdown to html
    if (!isHtml && !isConverted) {
      const newContent = content || DEFAULT_TEMPLATE;
      onChange(marked(newContent));
      setIsConverted(true);
    }
  }, [content, onChange, isConverted, isHtml]);

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image', 'code-block'],
      ['clean'],
    ],
  };

  // While converting, we can show empty. The useEffect will trigger a rerender with html.
  const htmlToShow = isHtml ? content : '';

  return (
    <EditorContainer>
      <ReactQuill
        theme="snow"
        value={htmlToShow}
        onChange={onChange}
        modules={modules}
        placeholder="Start writing your notes..."
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      />
    </EditorContainer>
  );
});
